import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// /home/ubuntu/avaai_dashboard/source/src/pages/games/CheckersGame.tsx
import { useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import "../../styles/GameLayout.css";
import { useNavigate } from "react-router-dom";
const BOARD_SIZE = 8;
function isPlayableSquare(x, y) {
    // Standard checkers: pieces live only on playable dark squares.
    return (x + y) % 2 === 1;
}
function makeInitialPieces() {
    const init = [];
    let id = 0;
    // Ava / Blue: back 3 rows.
    for (let y = 0; y <= 2; y++) {
        for (let x = 0; x < BOARD_SIZE; x++) {
            if (isPlayableSquare(x, y)) {
                init.push({ id: id++, x, y, color: "blue", king: false });
            }
        }
    }
    // User / Red: front 3 rows.
    for (let y = 5; y <= 7; y++) {
        for (let x = 0; x < BOARD_SIZE; x++) {
            if (isPlayableSquare(x, y)) {
                init.push({ id: id++, x, y, color: "red", king: false });
            }
        }
    }
    return init;
}
function inside(x, y) {
    return x >= 0 && y >= 0 && x < BOARD_SIZE && y < BOARD_SIZE;
}
function occupant(all, x, y) {
    return all.find((p) => p.x === x && p.y === y);
}
function dirsFor(piece, jump = false) {
    const step = jump ? 2 : 1;
    if (piece.king)
        return [[step, step], [-step, step], [step, -step], [-step, -step]];
    const dy = piece.color === "red" ? -step : step;
    return [[step, dy], [-step, dy]];
}
function legalMovesFor(piece, all, jumpsOnly = false) {
    const moves = [];
    for (const [dx, dy] of dirsFor(piece, false)) {
        const x = piece.x + dx;
        const y = piece.y + dy;
        if (!jumpsOnly && inside(x, y) && !occupant(all, x, y)) {
            moves.push({ x, y });
        }
    }
    for (const [dx, dy] of dirsFor(piece, true)) {
        const x = piece.x + dx;
        const y = piece.y + dy;
        const midX = piece.x + dx / 2;
        const midY = piece.y + dy / 2;
        const jumped = occupant(all, midX, midY);
        if (inside(x, y) && !occupant(all, x, y) && jumped && jumped.color !== piece.color) {
            moves.push({ x, y, captureId: jumped.id });
        }
    }
    return moves;
}
function allLegalMoves(color, all) {
    const pieces = all.filter((p) => p.color === color);
    const jumps = pieces.flatMap((p) => legalMovesFor(p, all, true)
        .filter((m) => m.captureId !== undefined)
        .map((m) => ({ piece: p, move: m })));
    if (jumps.length)
        return jumps;
    return pieces.flatMap((p) => legalMovesFor(p, all, false).map((m) => ({ piece: p, move: m })));
}
function winnerFor(all) {
    const redPieces = all.some((p) => p.color === "red");
    const bluePieces = all.some((p) => p.color === "blue");
    if (!redPieces)
        return "blue";
    if (!bluePieces)
        return "red";
    if (allLegalMoves("red", all).length === 0)
        return "blue";
    if (allLegalMoves("blue", all).length === 0)
        return "red";
    return null;
}
export default function CheckersGame() {
    const navigate = useNavigate();
    const [pieces, setPieces] = useState(() => makeInitialPieces());
    const [selectedId, setSelectedId] = useState(null);
    const [turn, setTurn] = useState("red");
    const [winner, setWinner] = useState(null);
    const [message, setMessage] = useState("You are red. Ava is blue. Red moves first.");
    const [aiLevel, setAiLevel] = useState(2);
    const selected = pieces.find((p) => p.id === selectedId) || null;
    const forcedMoves = useMemo(() => allLegalMoves(turn, pieces).filter((x) => x.move.captureId !== undefined), [turn, pieces]);
    function applyMove(piece, move, nextTurn = true) {
        let updated = pieces
            .filter((p) => p.id !== move.captureId)
            .map((p) => p.id === piece.id
            ? {
                ...p,
                x: move.x,
                y: move.y,
                king: p.king || (p.color === "red" && move.y === 0) || (p.color === "blue" && move.y === 7),
            }
            : p);
        const moved = updated.find((p) => p.id === piece.id);
        const chainJumps = move.captureId !== undefined
            ? legalMovesFor(moved, updated, true).filter((m) => m.captureId !== undefined)
            : [];
        const win = winnerFor(updated);
        setPieces(updated);
        if (win) {
            setWinner(win);
            setSelectedId(null);
            setMessage(win === "red" ? "Congratulations! You beat Ava. Want a rematch?" : "Ava wins. Good game — want a rematch?");
            return;
        }
        if (chainJumps.length && piece.color === "red") {
            setSelectedId(moved.id);
            setMessage("Forced jump: you must keep jumping with the same piece.");
            return;
        }
        setSelectedId(null);
        if (nextTurn) {
            const nt = piece.color === "red" ? "blue" : "red";
            setTurn(nt);
            setMessage(nt === "blue" ? "Ava is thinking..." : "Your turn.");
        }
    }
    function handleSquareClick(x, y) {
        if (winner || turn !== "red")
            return;
        if (selected) {
            const legal = legalMovesFor(selected, pieces, forcedMoves.length > 0);
            const move = legal.find((m) => m.x === x && m.y === y);
            if (!move) {
                setMessage(forcedMoves.length ? "A jump is available, so you must jump." : "That move is not legal.");
                return;
            }
            applyMove(selected, move);
            return;
        }
        const piece = occupant(pieces, x, y);
        if (!piece || piece.color !== "red")
            return;
        if (forcedMoves.length && !forcedMoves.some((fm) => fm.piece.id === piece.id)) {
            setMessage("Forced jump rule: select a piece that can jump.");
            return;
        }
        setSelectedId(piece.id);
        setMessage(forcedMoves.length ? "Jump available — forced jump is active." : "Piece selected.");
    }
    useEffect(() => {
        if (winner || turn !== "blue")
            return;
        const timer = window.setTimeout(() => {
            const legal = allLegalMoves("blue", pieces);
            if (!legal.length) {
                setWinner("red");
                setMessage("Congratulations! Ava has no legal moves. Want a rematch?");
                return;
            }
            const jumps = legal.filter((x) => x.move.captureId !== undefined);
            const choices = jumps.length ? jumps : legal;
            // Ava prefers jumps and kinging, then random among best candidates.
            const scored = choices.map((choice) => {
                let score = choice.move.captureId !== undefined ? 100 : 0;
                if (!choice.piece.king && choice.move.y === 7)
                    score += 50;
                score += Math.floor(Math.random() * (aiLevel + 2));
                return { ...choice, score };
            });
            scored.sort((a, b) => b.score - a.score);
            const best = scored[0];
            applyMove(best.piece, best.move);
        }, 650);
        return () => window.clearTimeout(timer);
    }, [turn, pieces, winner, aiLevel]);
    function restart() {
        setPieces(makeInitialPieces());
        setSelectedId(null);
        setTurn("red");
        setWinner(null);
        setMessage("New game. You are red. Ava is blue. Red moves first.");
    }
    return (_jsxs("div", { className: "game-hub-container", children: [_jsx("h1", { className: "neon-title", children: "\uD83D\uDD79\uFE0F AVAAIO Checkers" }), _jsxs("p", { className: "neon-subtitle", children: ["Turn: ", _jsx("span", { style: { color: turn === "red" ? "#ff3366" : "#00e0ff" }, children: turn === "red" ? "YOU / RED" : "AVA / BLUE" })] }), _jsx("p", { className: "neon-subtitle", children: message }), winner && (_jsxs("div", { children: [_jsx("h2", { className: "neon-title", children: winner === "red" ? "🎉 Congratulations — You Win!" : "🤖 Ava Wins!" }), _jsx("p", { className: "neon-subtitle", children: "Want a rematch?" })] })), _jsx("div", { style: { width: "88%", height: "70vh", margin: "20px auto" }, children: _jsxs(Canvas, { camera: { position: [4, 9, 10], fov: 48 }, children: [_jsx("color", { attach: "background", args: ["black"] }), _jsx("ambientLight", { intensity: 0.55 }), _jsx("pointLight", { position: [10, 10, 10], color: "#00ffff", intensity: 1.1 }), _jsx(OrbitControls, { enablePan: false, enableZoom: true }), [...Array(BOARD_SIZE)].map((_, y) => [...Array(BOARD_SIZE)].map((_, x) => {
                            const dark = isPlayableSquare(x, y);
                            const piece = occupant(pieces, x, y);
                            const isSelected = selectedId !== null && piece?.id === selectedId;
                            return (_jsxs("mesh", { position: [x - 3.5, 0, y - 3.5], onClick: () => handleSquareClick(x, y), children: [_jsx("boxGeometry", { args: [1, 0.1, 1] }), _jsx("meshStandardMaterial", { emissive: isSelected ? "#ffff00" : dark ? "#111111" : "#00ffff", emissiveIntensity: isSelected ? 0.8 : dark ? 0.04 : 0.28, color: dark ? "#050505" : "#003344" }), piece && (_jsxs("group", { position: [0, 0.22, 0], children: [_jsxs("mesh", { children: [_jsx("cylinderGeometry", { args: [0.38, 0.38, 0.18, 40] }), _jsx("meshStandardMaterial", { emissive: piece.color === "red" ? "#ff3366" : "#00e0ff", emissiveIntensity: 0.85, color: piece.color === "red" ? "#ff3366" : "#00e0ff" })] }), piece.king && (_jsxs(_Fragment, { children: [_jsxs("mesh", { position: [0, 0.16, 0], children: [_jsx("torusGeometry", { args: [0.24, 0.045, 16, 80] }), _jsx("meshStandardMaterial", { emissive: "#ffff00", emissiveIntensity: 1, color: "#ffff00" })] }), _jsx(Text, { position: [0, 0.33, 0], fontSize: 0.22, color: "#ffff00", anchorX: "center", anchorY: "middle", children: "K" })] }))] }))] }, `${x}-${y}`));
                        }))] }) }), _jsx("div", { style: { marginTop: "20px" }, children: _jsxs("label", { children: ["Ava Intelligence Level: ", aiLevel, _jsx("input", { type: "range", min: "1", max: "5", value: aiLevel, onChange: (e) => setAiLevel(parseInt(e.target.value)) })] }) }), _jsxs("div", { style: { marginTop: "20px" }, children: [_jsx("button", { className: "neon-btn", onClick: restart, children: "\uD83D\uDD04 Rematch" }), _jsx("button", { className: "exit-btn", onClick: () => navigate("/games"), children: "\u23EA Exit to Games" })] })] }));
}
